import { ApolloError } from "@apollo/client";
import type { Icons } from "components/icon/Icon";
import type { GraphQLFormattedError } from "graphql";
import { FETCH_FAILURE_MESSAGES } from "utils/errors";

export type ErrorFields = {
  statusCode?: number;
  path?: string;
  message?: string;
  iconName?: Icons;
  code?: string;
};

/**
 * Whether `error` is an Apollo error whose failure is best explained by the
 * *client's* network being slow or unstable, rather than a server or GraphQL
 * problem:
 *  - the request exceeded our ApolloLinkTimeout ceiling (`networkError.timeout`
 *    is set by apollo-link-timeout), or
 *  - it died before reaching the server (a browser fetch failure) while the
 *    browser still reports itself online — a flaky/slow link, not a clean
 *    offline state.
 *
 * Used to swap EmptyState's technical "status code / contact support" treatment
 * for a connectivity-focused message the user can act on.
 */
export const isSlowNetworkError = (error: unknown): boolean => {
  if (!(error instanceof ApolloError)) return false;

  const networkError = error.networkError as
    | (Error & { timeout?: number })
    | null;
  if (!networkError) return false;

  // apollo-link-timeout annotates the error with the elapsed timeout (ms).
  if (typeof networkError.timeout === "number") return true;

  // A fetch that never reached the server while we appear online points at a
  // slow/unstable connection. A genuine offline state is handled elsewhere.
  const isFetchFailure = FETCH_FAILURE_MESSAGES.some((message) =>
    networkError.message?.includes(message),
  );
  return isFetchFailure && navigator.onLine;
};

const getSuggestedIcon = (statusCode?: number): Icons | undefined => {
  if (!statusCode) return "status/X/Square";
  if (statusCode >= 500 && statusCode < 600)
    return "elements/Database/Server-Cancel";
  if (statusCode >= 400 && statusCode < 500) return "elements/Computer/Cancel";
  return "status/X/Square";
};

// True when the API rejected the request because the session is missing or
// expired.
export const isUnauthorized = ({ extensions }: GraphQLFormattedError) =>
  extensions?.status === 401;

export const getErrorsFields = (error: ApolloError): ErrorFields[] => {
  // ApolloError.cause === graphQLErrors[0] when graphQLErrors is populated, so
  // we only consult cause for the wrapping network-error path (otherwise its
  // fields would replicate [0]'s onto its siblings).
  const networkError =
    error.graphQLErrors.length === 0 ? (error.cause as any) : null;
  const sourceErrors: readonly (GraphQLFormattedError | undefined)[] =
    networkError
      ? (networkError.result?.errors ?? [undefined])
      : error.graphQLErrors;

  const errorsFields = sourceErrors.map((e) => {
    const statusCode =
      networkError?.statusCode ??
      (e?.extensions?.status as number | undefined) ??
      networkError?.extensions?.status;

    return {
      statusCode,
      path: (networkError?.path ?? e?.path)?.join("."),
      message:
        e?.message ??
        networkError?.message ??
        error.message ??
        "An unknown error occurred",
      iconName: getSuggestedIcon(statusCode),
      code: (e?.extensions?.code ?? networkError?.extensions?.code) as
        | string
        | undefined,
    };
  });

  return errorsFields;
};

/**
 * Whether the backend message on this status is the user's business: client
 * errors name something they can fix (a bad value, a missing resource).
 * Anything else (5xx, network, unknown) is internal detail.
 */
export const isClientError = (statusCode?: number): boolean =>
  statusCode !== undefined && statusCode >= 400 && statusCode < 500;

// Returns the backend message only for client errors the user can act on
// (HTTP 4xx); for anything else (5xx, network, unknown) it returns the generic
// fallback so we never expose internal backend errors to users. Accepts an
// ApolloError, a GraphQL errors array, or any other thrown value.
export function getUserFacingErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const fields: ErrorFields[] =
    error instanceof ApolloError
      ? getErrorsFields(error)
      : Array.isArray(error)
        ? (error as readonly GraphQLFormattedError[]).map((e) => ({
            statusCode: e?.extensions?.status as number | undefined,
            message: e?.message,
          }))
        : [];

  const clientError = fields.find(
    (field) => isClientError(field.statusCode) && field.message,
  );

  return clientError?.message ?? fallback;
}

// The message list a page shows with the error: client errors only, since a
// 5xx body is internal detail. The support draft (`buildSupportSearch`) still
// carries the raw message, because support needs it.
export const getErrorText = (error: ApolloError): string[] =>
  getErrorsFields(error)
    .filter(({ statusCode }) => isClientError(statusCode))
    .map(({ path, message }) => `${path ? `${path} - ` : ""}${message}`);

export const buildSupportSearch = (
  sourcePath: string,
  errors: ErrorFields[],
  serviceId?: string,
) => {
  const lines: string[] = [`Page: ${sourcePath}`];
  errors.forEach(({ path, statusCode, message }: ErrorFields) => {
    if (path) lines.push(`GraphQL path: ${path}`);
    if (statusCode) lines.push(`Status code: ${statusCode}`);
    lines.push(`Error: ${message}`);
  });

  return {
    subject: `I experienced a Tiger Data Console error`,
    description: `I encountered an error with the following details:\n${lines.join("\n")}\n\n`,
    ...(serviceId ? { serviceId } : {}),
  };
};
